import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modalIdleWarningOpen, modalIdleWarningTick, modalIdleWarningClose } from "../actions/modals";
import { http } from "../lib/http";
import { getIdleConfig } from "./config";

const KEY_DEADLINE = "idle:deadline"; // opcional p/ multi-aba
const CHANNEL      = "global-activity";

function parseJwtExp(token) {
  try {
    var payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (e) { return null; }
}

export default function IdleManager() {
  const dispatch = useDispatch();
  const isWarningOpen = useSelector(function (s) {
    return (s && s.modals) ? s.modals.modalIdleWarning : false;
  });

  const bcRef           = useRef(null);
  const warnTimerRef    = useRef(null);
  const logoutTimerRef  = useRef(null);
  const tickIntervalRef = useRef(null);
  const deadlineRef     = useRef(null); // timestamp em ms
  const logoutOnceRef   = useRef(false);
  const wasAuthedRef    = useRef(false);

  // tempos dinâmicos por rota (inicializados com defaults)
  const idleMsRef       = useRef(10 * 60 * 1000);
  const warnBeforeRef   = useRef(2  * 60 * 1000);
  const currentPathRef  = useRef(null); // para detectar mudança de pathname

  function isAuthenticated() {
    try { return !!localStorage.getItem("login"); } catch (e) { return false; }
  }

  function applyConfigForPath(pathname) {
    try {
      if (!pathname && typeof window !== "undefined") pathname = window.location && window.location.pathname;
      if (!pathname) pathname = "/";

      var cfg = getIdleConfig(pathname);

      idleMsRef.current     = (cfg && cfg.idleMs) ? cfg.idleMs : idleMsRef.current;
      warnBeforeRef.current = (cfg && cfg.warnMs) ? cfg.warnMs : warnBeforeRef.current;
      currentPathRef.current = pathname;
    } catch (e) {
      // fallback de segurança
      idleMsRef.current     = 10 * 60 * 1000;
      warnBeforeRef.current = 2  * 60 * 1000;
    }
  }

  function clearTimers() {
    if (warnTimerRef.current)    { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (logoutTimerRef.current)  { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (tickIntervalRef.current) { clearInterval(tickIntervalRef.current); tickIntervalRef.current = null; }
  }

  function postBC(msg) {
    if (bcRef.current) {
      try { bcRef.current.postMessage(msg); } catch (e) {}
    }
  }

  function scheduleTimers() {
    clearTimers();
    var now = Date.now();
    var deadline = deadlineRef.current;
    if (!deadline || deadline <= now) return;

    var warnAt    = deadline - warnBeforeRef.current;
    var msToWarn  = Math.max(0, warnAt - now);
    var msToLogout= Math.max(0, deadline - now);

    // aviso
    warnTimerRef.current = setTimeout(function () {
      var remaining = Math.ceil((deadlineRef.current - Date.now()) / 1000);
      if (remaining < 0) remaining = 0;
      dispatch(modalIdleWarningOpen(remaining));

      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = setInterval(function () {
        var r = Math.ceil((deadlineRef.current - Date.now()) / 1000);
        if (r < 0) r = 0;
        dispatch(modalIdleWarningTick(r));
      }, 1000);
    }, msToWarn);

    // logout
    logoutTimerRef.current = setTimeout(function () {
      doLogout("idle");
    }, msToLogout);
  }

  function stampActivity() {
    if (!isAuthenticated()) return;
    // define novo deadline = agora + idleMs atual
    var next = Date.now() + idleMsRef.current;
    deadlineRef.current = next;

    // persistência p/ multi-aba / reload
    try { localStorage.setItem(KEY_DEADLINE, String(next)); } catch (e) {}
    postBC({ t: "deadline-update", deadline: next });

    // fecha aviso se estiver aberto
    try { dispatch(modalIdleWarningClose()); } catch (e) {}

    scheduleTimers();
  }

  function doLogout(reason) {
    if (logoutOnceRef.current) return;
    logoutOnceRef.current = true;

    clearTimers();

    // evita redirect duplicado com interceptor
    try { window.__REDIRECTING__ = true; } catch (e) {}

    // avisa outras abas
    postBC({ t: "force-logout" });

    // aborta requisições ativas, se houver
    try { if (typeof window.__ABORT_ALL__ === "function") window.__ABORT_ALL__(); } catch (e) {}

    // limpa storage
    try { localStorage.removeItem("login"); } catch (e) {}
    try { localStorage.removeItem(KEY_DEADLINE); } catch (e) {}
    try { sessionStorage.clear(); } catch (e) {}

    // fecha modal
    try { dispatch(modalIdleWarningClose()); } catch (e) {}

    if (typeof window.focus === "function") window.focus();
    window.location.href = "/?reason=" + (reason || "idle");
  }

  async function tryRefresh() {
    if (!isAuthenticated()) return;
    // se falta pouco para o auto-logout, não refresca
    var now = Date.now();
    if (deadlineRef.current && (deadlineRef.current - now) <= 60 * 1000) return;

    try {
      var res = await http.post("/refresh");
      var newToken = (res && res.data && (res.data.data || res.data.token))
        ? (res.data.data || res.data.token)
        : null;
      if (newToken) {
        localStorage.setItem("login", newToken);
      }
    } catch (e) {
      doLogout("expired");
    }
  }

  useEffect(function () {
    // BroadcastChannel
    if ("BroadcastChannel" in window) {
      try { bcRef.current = new BroadcastChannel(CHANNEL); } catch (e) {}
    }
    if (bcRef.current) {
      bcRef.current.addEventListener("message", function (ev) {
        var data = ev ? ev.data : null;
        var t = data ? data.t : null;
        if (t === "force-logout") doLogout("idle"); // protegido por logoutOnceRef
        if (t === "deadline-update") {
          if (!isAuthenticated()) return;
          var d = data && data.deadline ? Number(data.deadline) : 0;
          if (isFinite(d) && d > Date.now()) {
            deadlineRef.current = d;
            try { localStorage.setItem(KEY_DEADLINE, String(d)); } catch (e) {}
            scheduleTimers();
          }
        }
      });
    }

    // listeners de atividade
    var passive = { passive: true };
    var handler = function () { stampActivity(); };
    ["mousemove","mousedown","keydown","scroll","touchstart"].forEach(function (ev) {
      window.addEventListener(ev, handler, passive);
    });
    window.addEventListener("focus", handler);
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden) handler();
    });

    // boot: aplica política da rota atual e define deadline inicial
    applyConfigForPath((typeof window !== "undefined" && window.location && window.location.pathname) ? window.location.pathname : "/");

    if (isAuthenticated()) {
      wasAuthedRef.current = true;
      // tenta pegar deadline persistido (multi-aba / reload)
      var raw = localStorage.getItem(KEY_DEADLINE);
      var d = Number(raw);
      if (isFinite(d) && d > Date.now()) {
        deadlineRef.current = d;
      } else {
        deadlineRef.current = Date.now() + idleMsRef.current;
        try { localStorage.setItem(KEY_DEADLINE, String(deadlineRef.current)); } catch (e) {}
      }
      scheduleTimers();
    } else {
      wasAuthedRef.current = false;
    }

    // polling leve: refresh de token + detecção de mudança de rota
    var lastPathname = (typeof window !== "undefined" && window.location) ? window.location.pathname : "/";
    var refreshInt = setInterval(function () {
      // refresh do token
      if (isAuthenticated()) {
        var token = localStorage.getItem("login");
        if (token) {
          var expMs = parseJwtExp(token);
          if (expMs) {
            var timeToExp = expMs - Date.now();
            if (timeToExp <= 0) { doLogout("expired"); return; }
            if (timeToExp <= 120 * 1000) { tryRefresh(); }
          }
        }
      }

      // rota mudou? (sem depender do Router)
      var currentPathname = (typeof window !== "undefined" && window.location) ? window.location.pathname : "/";
      if (currentPathname !== lastPathname) {
        lastPathname = currentPathname;

        // aplica nova política e reagenda
        applyConfigForPath(currentPathname);

        if (isAuthenticated()) {
          // estratégia simples: resetar prazo ao trocar de rota (agora + idle da rota)
          deadlineRef.current = Date.now() + idleMsRef.current;
          try { localStorage.setItem(KEY_DEADLINE, String(deadlineRef.current)); } catch (e) {}
          scheduleTimers();
        }
      }
    }, 1000);

    return function () {
      ["mousemove","mousedown","keydown","scroll","touchstart"].forEach(function (ev) {
        window.removeEventListener(ev, handler);
      });
      window.removeEventListener("focus", handler);
      if (bcRef.current) { try { bcRef.current.close(); } catch (e) {} }
      clearTimers();
      clearInterval(refreshInt);
    };
  }, []); // mount

  // tick do aviso só quando aberto
  useEffect(function () {
    if (!isWarningOpen && tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }, [isWarningOpen]);

  return null;
}
